import { z } from 'zod';

const ragConfigSchema = z.object({
  top_k: z.number().default(5),
  score_threshold: z.number().default(0.3),
  embedding_model: z.string().trim().default('text-embedding-ada-002'),
  embedding_model_provider: z.string().trim().default('azure'),
  chunking_strategy: z.string().trim().default('semantic'),
  enable_query_enhancement: z.boolean().default(true),
  enable_rerank: z.boolean().default(true),
  dense_weight: z.number().default(0.9),
  keyword_weight: z.number().default(0.1),
});

const llmConfigSchema = z.object({
  provider: z.string().trim(),
  model_name: z.string().trim(),
  is_blocks_provider: z.boolean(),
  model_id: z.string().trim().nullable().optional(),
  // model_type: z.string().trim().optional().default("chat"),
  base_prompt: z.string().trim().nullable().optional(),
  base_prompt_token: z.number().nullable().optional(),
  api_key: z.string(),
  api_base: z.string().trim(),
  api_version: z.string().trim(),
  temperature: z.number().default(0.7),
  max_tokens: z.number().nullable(),
});

const memoryConfigSchema = z.object({
  last_n_conversations: z.number().default(5),
  enable_summary: z.boolean().default(false),
  is_blocks_provider: z.boolean().default(false),
  enable_questions_suggestions: z.boolean().optional().default(false),
});

// inside @blocks-ai/components/agent-behavior-settings/utils
export const guardrailSchema = z.object({
  enable_pii_detection: z.boolean().default(false),
  enable_injection_detection: z.boolean().default(false),
  enable_output_safety: z.boolean().default(false),
  enable_keyword_check: z.boolean().default(false),
  enable_custom_rules: z.boolean().default(false),

  enable_pre_validation: z.boolean().default(true),
  enable_post_validation: z.boolean().default(true),

  banned_keywords: z.array(z.string().trim()).default([]),
  banned_patterns: z.array(z.string().trim()).default([]),

  custom_rules: z
    .array(
      z.object({
        name: z.string().trim().optional().default(''),
        pattern: z.string().trim().optional().default(''),
        severity: z.string().trim().optional().default('medium'),
        auto_block: z.boolean().default(false),
        message: z.string().trim().optional(),
        enable_pre: z.boolean().default(true),
        enable_post: z.boolean().default(true),
      })
    )
    .default([]),

  input_risk_threshold: z.number().min(0).max(1).default(0.7),
  output_risk_threshold: z.number().min(0).max(1).default(0.8),
});

const widgetSchema = z.object({
  enable_predefined_questions: z.boolean().optional().default(false),
  // enable_questions_suggestions: z.boolean().optional().default(false),
  predefined_questions: z.array(z.string().trim()).optional().default([]),
  greeting: z.string().trim().optional().default(''),
});

export const agentBehaviorSettingsFormSchema = z
  .object({
    name: z.string().trim(),
    description: z.string().trim().optional(),
    response_type: z.string().trim().optional().default('text'),
    response_format: z.string().trim().nullable().optional().default(null),
    llm_config: llmConfigSchema,
    memory_config: memoryConfigSchema,
    enable_rag: z.boolean().default(true),
    rag_config: ragConfigSchema,
    enable_tools: z.boolean().optional(),
    tool_ids: z.array(z.string().trim()).default([]),
    enable_guardrails: z.boolean().optional(),
    guardrail: guardrailSchema,
    widget: widgetSchema,
    enable_knowledge_base: z.boolean().optional(),
    kb_ids: z.array(z.string().trim()).default([]),
    folder_ids: z.array(z.string().trim()).default([]),
    enable_memory: z.boolean().default(true),
    enable_human_handoff: z.boolean().default(false),
  })
  .superRefine(() => {
    // if (!data.use_default_guardrail_instructions) {
    //   if (!data.gurdrail_instructions || data.gurdrail_instructions.trim().length === 0) {
    //     ctx.addIssue({
    //       code: z.ZodIssueCode.custom,
    //       message: "Guardrail instructions are required",
    //       path: ["gurdrail_instructions"],
    //     });
    //   }
    // }
    // if (!data.use_default_output_format) {
    //   if (!data.output_format_instructions || data.output_format_instructions.trim().length === 0) {
    //     ctx.addIssue({
    //       code: z.ZodIssueCode.custom,
    //       message: "Output format instructions are required",
    //       path: ["output_format_instructions"],
    //     });
    //   }
    // }
    // if (!data.use_default_safety_policy) {
    //   if (!data.safety_and_content_policy || data.safety_and_content_policy.trim().length === 0) {
    //     ctx.addIssue({
    //       code: z.ZodIssueCode.custom,
    //       message: "Safety and content policy is required",
    //       path: ["safety_and_content_policy"],
    //     });
    //   }
    // }
  });

export type AgentBehaviorSettingsForm = z.infer<typeof agentBehaviorSettingsFormSchema>;

export const agentBehaviorSettingsDefaultValue: AgentBehaviorSettingsForm = {
  name: '',
  description: '',
  response_type: 'text',
  response_format: null,
  llm_config: {
    api_base: '',
    api_key: '',
    api_version: '',
    is_blocks_provider: true,
    model_id: null,
    model_name: '',
    // model_type: "chat",
    base_prompt: null,
    provider: '',
    temperature: 0.7,
    max_tokens: null,
  },
  rag_config: {
    top_k: 5,
    score_threshold: 0.3,
    embedding_model: 'text-embedding-ada-002',
    embedding_model_provider: 'azure',
    chunking_strategy: 'semantic',
    enable_query_enhancement: true,
    enable_rerank: true,
    dense_weight: 0.9,
    keyword_weight: 0.1,
  },
  memory_config: {
    last_n_conversations: 5,
    enable_summary: false,
    is_blocks_provider: false,
    enable_questions_suggestions: false,
  },
  guardrail: {
    enable_pii_detection: false,
    enable_injection_detection: false,
    enable_output_safety: false,
    enable_keyword_check: false,
    enable_custom_rules: false,
    enable_pre_validation: true,
    enable_post_validation: true,
    banned_keywords: [],
    banned_patterns: [],
    custom_rules: [],
    input_risk_threshold: 0.7,
    output_risk_threshold: 0.8,
  },
  widget: {
    enable_predefined_questions: false,
    // enable_questions_suggestions: false,
    predefined_questions: [],
    greeting: '',
  },
  enable_knowledge_base: false,
  enable_tools: false,
  enable_memory: false,
  enable_human_handoff: false,
  enable_rag: false,
  enable_guardrails: false,
  tool_ids: [],
  kb_ids: [],
  folder_ids: [],
};
